import type { GameState } from '@/types/game';
import { useCallback, useEffect, useRef, useState } from 'react';

interface CameraConfig {
  zoom: number;
  offsetX: number;
  offsetY: number;
  transition: string;
}

export const useCameraZoom = (gameState: GameState, restartCounter?: number) => {
  const timerIdsRef = useRef<number[]>([]);

  const [cameraConfig, setCameraConfig] = useState<CameraConfig>({
    zoom: 0.9,
    offsetX: 0,
    offsetY: 0,
    transition: 'transform 0ms linear',
  });

  const [cameraPhase, setCameraPhase] = useState<'initial' | 'entrance' | 'playing' | 'exit' | 'complete'>('initial');

  const clearTimers = useCallback(() => {
    timerIdsRef.current.forEach(id => window.clearTimeout(id));
    timerIdsRef.current = [];
  }, []);

  const queueTimer = useCallback((callback: () => void, delayMs: number) => {
    const timerId = window.setTimeout(() => {
      timerIdsRef.current = timerIdsRef.current.filter(id => id !== timerId);
      callback();
    }, delayMs);
    timerIdsRef.current.push(timerId);
  }, []);

  const getBoardMetrics = useCallback(() => {
    const boardElement = document.querySelector('.game-board-container') as HTMLElement | null;
    if (!boardElement) return null;

    const boardW = boardElement.clientWidth;
    const boardH = boardElement.clientHeight;
    if (boardW === 0 || boardH === 0) return null;

    const styles = window.getComputedStyle(boardElement);
    const cssCell = parseFloat(styles.getPropertyValue('--cell'));
    const colsFromAttr = Number(boardElement.dataset.cols || 0);
    const rowsFromAttr = Number(boardElement.dataset.rows || 0);
    const inferredCellWidth = colsFromAttr > 0 ? boardW / colsFromAttr : 0;
    const inferredCellHeight = rowsFromAttr > 0 ? boardH / rowsFromAttr : 0;
    const fallbackCell = Math.max(inferredCellWidth, inferredCellHeight, 1);
    const cellSize = Number.isFinite(cssCell) && cssCell > 0 ? cssCell : fallbackCell;

    return { boardW, boardH, cellSize };
  }, []);

  // Function to center camera on a position
  const centerOnPosition = useCallback((position: [number, number]) => {
    const metrics = getBoardMetrics();
    if (!metrics) return { offsetX: 0, offsetY: 0 };

    const { boardW, boardH, cellSize } = metrics;
    const posX = position[0] * cellSize + cellSize / 2;
    const posY = position[1] * cellSize + cellSize / 2;
    const offsetX = boardW / 2 - posX;
    const offsetY = boardH / 2 - posY;

    return { offsetX, offsetY };
  }, [getBoardMetrics]);

  // Start entrance sequence
  const startEntrance = useCallback(() => {
    clearTimers();
    setCameraPhase('entrance');
    const { offsetX, offsetY } = centerOnPosition(gameState.position);
    setCameraConfig({
      zoom: 1.55,
      offsetX,
      offsetY,
      transition: 'transform 820ms cubic-bezier(0.22, 1, 0.36, 1)',
    });
  }, [centerOnPosition, clearTimers, gameState.position]);

  // Complete entrance and zoom out
  const completeEntrance = useCallback(() => {
    setCameraPhase('playing');
    setCameraConfig({
      zoom: 1.02,
      offsetX: 0,
      offsetY: 0,
      transition: 'transform 960ms cubic-bezier(0.22, 1, 0.36, 1)',
    });
  }, []);

  // Start exit sequence
  const startExit = useCallback(() => {
    clearTimers();
    setCameraPhase('exit');
    const { offsetX, offsetY } = centerOnPosition(gameState.end);
    setCameraConfig({
      zoom: 1.88,
      offsetX,
      offsetY,
      transition: 'transform 1200ms cubic-bezier(0.22, 1, 0.36, 1)',
    });
  }, [centerOnPosition, clearTimers, gameState.end]);

  // Complete exit: reset zoom to neutral and allow modal to show
  const completeExit = useCallback(() => {
    setCameraConfig({
      zoom: 0.98,
      offsetX: 0,
      offsetY: 0,
      transition: 'transform 640ms cubic-bezier(0.22, 1, 0.36, 1)',
    });
    setCameraPhase('complete');
  }, []);

  // Check if player is at end position AND all collectibles are collected (level actually complete)
  useEffect(() => {
    if (gameState.isPlaying && cameraPhase === 'playing') {
      const atEnd = gameState.position[0] === gameState.end[0] &&
        gameState.position[1] === gameState.end[1];

      const allCollected = !gameState.collectibles ||
        gameState.collectibles.length === 0 ||
        gameState.collectibles.every((c: any) => c.isCollected);

      if (atEnd && allCollected) {
        startExit();
      }
    }
  }, [gameState.position, gameState.end, gameState.isPlaying, gameState.collectibles, cameraPhase, startExit]);

  // --- RESET EFFECTS (must run BEFORE entrance trigger) ---

  // Reset camera phase when level changes (or infinite wave changes)
  useEffect(() => {
    clearTimers();
    setCameraPhase('initial');
    setCameraConfig({
      zoom: 0.9,
      offsetX: 0,
      offsetY: 0,
      transition: 'transform 0ms linear',
    });
  }, [gameState.level, gameState.infiniteState?.wave, clearTimers]);

  // Reset camera phase when restart counter changes (for same-level restarts)
  useEffect(() => {
    if (restartCounter !== undefined && restartCounter > 0) {
      clearTimers();
      setCameraPhase('initial');
      setCameraConfig({
        zoom: 0.9,
        offsetX: 0,
        offsetY: 0,
        transition: 'transform 0ms linear',
      });
    }
  }, [restartCounter, clearTimers]);

  // Also reset when game transitions from not playing to playing (restart scenario)
  useEffect(() => {
    if (gameState.isPlaying && cameraPhase !== 'initial' && cameraPhase !== 'entrance' && cameraPhase !== 'playing') {
      clearTimers();
      setCameraPhase('initial');
    }
  }, [gameState.isPlaying, cameraPhase, clearTimers]);

  // --- ENTRANCE TRIGGER (must run AFTER all reset effects) ---
  // React runs effects in declaration order within a single commit.
  // This effect must be declared after the reset effects above, otherwise
  // the reset effects would clearTimers() and cancel the entrance timer.
  useEffect(() => {
    if (gameState.isPlaying && cameraPhase === 'initial') {
      setCameraConfig({
        zoom: 0.9,
        offsetX: 0,
        offsetY: 0,
        transition: 'transform 0ms linear',
      });

      queueTimer(() => {
        startEntrance();
      }, 140);
    }
  }, [gameState.isPlaying, cameraPhase, startEntrance, queueTimer]);

  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    cameraConfig,
    cameraPhase,
    startEntrance,
    completeEntrance,
    startExit,
    completeExit,
    isLocked: cameraPhase === 'entrance' || cameraPhase === 'exit', // Lock movement during animations
    // Transform style to apply to game board
    cameraStyle: {
      transform: `translate3d(${cameraConfig.offsetX}px, ${cameraConfig.offsetY}px, 0) scale(${cameraConfig.zoom})`,
      transition: cameraConfig.transition,
      transformOrigin: 'center center',
      willChange: 'transform',
    },
  };
};
